import React from 'react'
import moment from 'moment'
import { parseAsync } from 'json2csv'
import {
  iconStatusNetwork,
  iconStatusSmartPhone,
  iconStatusSpot,
  iconStatusDongle,
  iconStatusNotFound,
  iconBatteryUnknown,
  iconBatteryFull,
  iconBatteryHalf,
  iconBatteryLittle,
  iconBatteryEmpty,
  itemIcon,
} from '../icons'
import { getDurationFrom, getFormattedDate } from './timeHelper'

moment.locale('ja')

export const getMamorioImageSrc = (mamorio: IMamorio | undefined, defaultIconSrc: string = itemIcon): string => {
  if (!mamorio?.image?.url || !mamorio.image.retina?.url || mamorio.image.retina.url === 'null') {
    return defaultIconSrc
  }
  return mamorio.image.retina.url
}

export const getUserName = (user: Pick<IUser, 'name' | 'email'>): string => user.name || user.email

export const isMamorio = (object: IMamorio): boolean => typeof object.minsaga !== 'undefined'
export const isDevice = (object: IMamorio): boolean => typeof object.minsaga === 'undefined'

export const getLastStatus = (mamorio: IMamorio): {
  imgSrc: string,
  text: string
} => {
  const lastLog = mamorio.recent_log

  if (!lastLog) {
    return {
      imgSrc: iconStatusNotFound,
      text: '発見情報がありません',
    }
  }

  // FIXME 情報公開の可否はサーバー側で判断すべきか？
  if (lastLog.group) {
    if (lastLog.spot && lastLog.spot.name) {
      return {
        imgSrc: iconStatusSpot,
        text: lastLog.spot.name,
      }
    }
    if (lastLog.dongle && lastLog.dongle.name) {
      return {
        imgSrc: iconStatusDongle,
        text: lastLog.dongle.name,
      }
    }
    if (lastLog.device && lastLog.device.name) {
      return {
        imgSrc: iconStatusSmartPhone,
        text: lastLog.device.name,
      }
    }
    if (lastLog.finder) {
      return {
        imgSrc: iconStatusSmartPhone,
        text: getUserName(lastLog.finder),
      }
    }
    return {
      imgSrc: iconStatusSmartPhone,
      text: 'メンバー',
    }
  }
  if (lastLog.finder) {
    return {
      imgSrc: iconStatusSmartPhone,
      text: getUserName(lastLog.finder),
    }
  }
  return {
    imgSrc: iconStatusNetwork,
    text: 'クラウドネットワーク',
  }
}

export const getMamoriosWithLocation = (mamorios: IMamorio[]): any[] => (
  mamorios.filter((mamorio) => mamorio.recent_log)
)

export const getRentalStatus = (mamorio: IMamorio): JSX.Element => {
  if (mamorio.is_rent === true) {
    return (
      <div className="status-rental rental">
        持出中
      </div>
    )
  }
  if (mamorio.is_rent === false) {
    return (
      <div className="status-rental returned">
        返却済
      </div>
    )
  }
  return (
    <div className="status-rental">
      -
    </div>
  )
}

export const getLostFoundStatus = (mamorio: IMamorio): JSX.Element => {
  if (mamorio.recent_log?.at && getDurationFrom(mamorio.recent_log?.at).asMinutes() < 30) {
    return (
      <div className="status-detection detected">
        検知
      </div>
    )
  }
  return (
    <div className="status-detection notdetected">
      未検知
    </div>
  )
}

export const getBatteryImageSrc = ({ estimated_remain_battery }: IMamorio): string => {
  if (estimated_remain_battery == null) {
    return iconBatteryUnknown
  }

  switch (true) {
    case estimated_remain_battery > 50:
      return iconBatteryFull
    case estimated_remain_battery > 20:
      return iconBatteryHalf
    case estimated_remain_battery > 0:
      return iconBatteryLittle
    default:
      return iconBatteryEmpty
  }
}

const handleBlob = (data: BlobPart[], options: BlobPropertyBag, callback: (url: string) => void) => {
  const url = URL.createObjectURL(new Blob(data, options))
  callback(url)
  URL.revokeObjectURL(url)
}

const CSV_EMPTY_VALUE = '-'

const toCsvValue = (value?: string | number | null): string | number => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return CSV_EMPTY_VALUE
  }

  return value
}

const formatCsvDate = (value?: string | null): string => (
  value ? moment(value).utcOffset(540).format('YYYY/MM/DD HH:mm') : CSV_EMPTY_VALUE
)

const getRentalStatusText = (mamorio: IMamorio): string => {
  if (mamorio.is_rent === true) return '持出中'
  if (mamorio.is_rent === false) return '返却済'
  return CSV_EMPTY_VALUE
}

type MamorioListCsvOptions = {
  includeRentalStatus?: boolean
  fileName?: string
}

export const buildMamorioListCsvRows = (
  mamorios: IMamorio[],
  options: Pick<MamorioListCsvOptions, 'includeRentalStatus'> = {},
) => (
  mamorios.map((mamorio) => {
    const recentLog = mamorio.recent_log
    const lastStatus = getLastStatus(mamorio)
    const includeRentalStatus = options.includeRentalStatus ?? false

    return {
      id: mamorio.id,
      major: mamorio.major,
      minor: mamorio.minor,
      major_minor: `${mamorio.major}-${mamorio.minor}`,
      name: toCsvValue(mamorio.name),
      memo: toCsvValue(mamorio.memo),
      status: toCsvValue(mamorio.status),
      ...(includeRentalStatus ? { rental_status: getRentalStatusText(mamorio) } : {}),
      owner_name: toCsvValue(mamorio.user ? getUserName(mamorio.user) : null),
      owner_email: toCsvValue(mamorio.user?.email),
      estimated_remain_battery: toCsvValue(mamorio.estimated_remain_battery),
      battery_life_resetted_at: formatCsvDate(mamorio.battery_life_resetted_at),
      recent_detected_at: formatCsvDate(recentLog?.at),
      recent_detected_by: lastStatus.text,
      recent_log_finder_name: toCsvValue(recentLog?.finder ? getUserName(recentLog.finder) : null),
      recent_log_finder_email: toCsvValue(recentLog?.finder?.email),
      latitude: toCsvValue(recentLog?.latitude),
      longitude: toCsvValue(recentLog?.longitude),
      precision: toCsvValue(recentLog?.precision),
      rssi: toCsvValue(recentLog?.rssi),
    }
  })
)

const buildMamorioListCsvFields = (includeRentalStatus: boolean) => [
  { label: 'MAMORIO ID', value: 'id' },
  { label: 'major', value: 'major' },
  { label: 'minor', value: 'minor' },
  { label: 'major-minor', value: 'major_minor' },
  { label: '名前', value: 'name' },
  { label: 'メモ', value: 'memo' },
  { label: 'ステータス', value: 'status' },
  ...(includeRentalStatus ? [{ label: '持ち出し状態', value: 'rental_status' }] : []),
  { label: '所持者', value: 'owner_name' },
  { label: '所持者メール', value: 'owner_email' },
  { label: '推定バッテリー残量', value: 'estimated_remain_battery' },
  { label: 'バッテリー交換日', value: 'battery_life_resetted_at' },
  { label: '最新発見日時', value: 'recent_detected_at' },
  { label: '最後に検知した端末', value: 'recent_detected_by' },
  { label: '発見ユーザー', value: 'recent_log_finder_name' },
  { label: '発見ユーザーメール', value: 'recent_log_finder_email' },
  { label: '緯度', value: 'latitude' },
  { label: '経度', value: 'longitude' },
  { label: '精度', value: 'precision' },
  { label: 'RSSI', value: 'rssi' },
]

export const generateMamorioListCSV = async (
  mamorios: IMamorio[],
  options: MamorioListCsvOptions = {},
) => {
  const includeRentalStatus = options.includeRentalStatus ?? false
  const fileName = options.fileName ?? `biz_plus_mamorios_${getFormattedDate(new Date(), 'YYYYMMDD_HHmmss')}.csv`
  const csv = await parseAsync(buildMamorioListCsvRows(mamorios, { includeRentalStatus }), {
    fields: buildMamorioListCsvFields(includeRentalStatus),
    withBOM: true,
  })

  handleBlob([`${csv}\n`], { type: 'text/csv;charset=utf-8' }, (url) => {
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
  })
}

export const generateRentalDigitCSV = async (qrCodes: IQrCode[]) => {
  const csv = await parseAsync(qrCodes, {
    fields: [
      {
        label: '名称',
        value: 'resource_name',
      },
      {
        label: 'digit_code',
        value: 'digit_code',
      },
      {
        label: 'code',
        value: 'digit_code',
      },
    ],
    withBOM: true,
  })
  console.error(csv)
  handleBlob([`${csv}\n`], { type: 'text/csv' }, (url) => {
    const a = document.createElement('a')
    a.href = url
    a.download = 'qrCodes.csv'
    a.click()
  })
}
